import { useEffect, useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BookOpen, TrendingUp, Users, ArrowRight, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

export default function InsightsSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          end: 'top 30%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.fromTo(
        headerRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 }
      );

      const cards = cardsRef.current?.querySelectorAll('.insight-card');
      if (cards) {
        tl.fromTo(
          cards,
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, stagger: 0.1 },
          '-=0.3'
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const featuredInsights = [
    {
      id: '1',
      title: 'The First 90 Days: How I Turn Around Struggling Engineering Teams',
      author: 'Marcus Chen',
      role: 'Fractional CTO',
      tags: ['engineering-leadership', 'turnaround'],
      views: 1247,
      likes: 89,
      preview: 'When I join a company as a fractional CTO, I have one goal: deliver measurable impact within 90 days...'
    },
    {
      id: '2',
      title: 'Pricing Your Fractional CFO Services: A Data-Driven Guide',
      author: 'Sarah Williams',
      role: 'Fractional CFO',
      tags: ['pricing', 'freelancing'],
      views: 892,
      likes: 67,
      preview: 'What 50 engagements taught me about rate setting and value-based pricing in the fractional economy...'
    },
    {
      id: '3',
      title: 'Why Most CMOs Fail in Their First 90 Days',
      author: 'David Park',
      role: 'Fractional CMO',
      tags: ['marketing-leadership', 'first-90-days'],
      views: 756,
      likes: 54,
      preview: 'The three traps that derail even experienced marketing leaders when they join a new company...'
    }
  ];

  return (
    <section
      ref={sectionRef}
      id="insights"
      className="relative py-24 bg-navy overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-teal/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full px-6 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal/10 rounded-full mb-6">
            <BookOpen className="w-4 h-4 text-teal" />
            <span className="text-sm font-medium text-teal">Fractional Insights</span>
          </div>
          
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
            Learn from the best fractional executives
          </h2>
          <p className="text-lg text-slate max-w-2xl mx-auto">
            Our CXOs publish real-world insights, frameworks, and lessons from the field. 
            Read their thinking before you hire them.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-6 mb-16 max-w-2xl mx-auto">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-teal" />
              <span className="text-2xl font-bold text-white">200+</span>
            </div>
            <p className="text-sm text-slate">Published Articles</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Users className="w-5 h-5 text-teal" />
              <span className="text-2xl font-bold text-white">50+</span>
            </div>
            <p className="text-sm text-slate">Verified CXO Writers</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-teal" />
              <span className="text-2xl font-bold text-white">15K</span>
            </div>
            <p className="text-sm text-slate">Monthly Readers</p>
          </div>
        </div>

        {/* Article Cards */}
        <div ref={cardsRef} className="grid md:grid-cols-3 gap-6 mb-12">
          {featuredInsights.map((insight) => (
            <div
              key={insight.id}
              className="insight-card surface-panel p-6 hover:border-teal/50 transition-all cursor-pointer group"
              onClick={() => navigate(`/insights/article/${insight.id}`)}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal to-blue-500 flex items-center justify-center text-white font-bold">
                  {insight.author[0]}
                </div>
                <div>
                  <p className="font-medium text-white text-sm">{insight.author}</p>
                  <p className="text-xs text-slate">{insight.role}</p>
                </div>
                <span className="ml-auto text-teal text-xs">✓</span>
              </div>

              <h3 className="font-heading text-lg font-semibold text-white mb-3 group-hover:text-teal transition-colors">
                {insight.title}
              </h3>

              <p className="text-sm text-slate mb-4 line-clamp-2">
                {insight.preview}
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                {insight.tags.map(tag => (
                  <span key={tag} className="px-2 py-1 bg-navy text-teal text-xs rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-4 text-xs text-slate">
                <span>{insight.views.toLocaleString()} views</span>
                <span className="flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  {insight.likes}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate('/insights')}
            className="btn-primary inline-flex items-center gap-2"
          >
            Browse All Insights
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* For CXOs CTA */}
        <div className="mt-16 p-8 bg-surface rounded-2xl border border-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">
                Are you a fractional CXO?
              </h3>
              <p className="text-slate">
                Publish your insights, build your audience, and get discovered by companies 
                before they even post a job.
              </p>
            </div>
            <button
              onClick={() => navigate('/register')}
              className="btn-secondary whitespace-nowrap flex items-center gap-2"
            >
              Start Writing
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
